import React, {Children, useEffect, useState} from 'react';
import {useHistory} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {VALIDATION_CONSTANTS as Labels} from "../../constants/LabelConstants";
import {EstateItem} from "../../model/EstateFlowchartTypes";
import {navItemSelector, saveSelector, setActiveNavItem} from "../../slices/NavigationSlice";
import {createNavConfig} from "../../utils/NavUtils";
import AlertBanner from "../AlertBanner/AlertBanner";
import FormFooter from "../FormFooter/FormFooter";
import FormHeader from "../FormHeader/FormHeader";
import FormNav from "../FormNav/FormNav";
import {NavItem} from "../FormNav/NavItem";
import "./_form.scss";
import {ValidationError, ValidationErrorType} from "../../validation/ValidationTypes";
import {EstateItemType} from "../../forms/EstateItemType";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";
import ScrollableContainer from "../../../components/ScrollableContainer/ScrollableContainer";

interface FormProps extends React.PropsWithChildren<any> {
    estateItem: EstateItem | undefined;
    estateItemType: EstateItemType
    title: string;
    subHeader?: string;
    pageConfig: NavItem[];
    linkConfig?: any;
    isFormChanged: boolean;
    validate: () => boolean;
    saveForm: () => Promise<boolean>;
    validationErrors: ValidationError[];
}

function Form({
                  estateItem, estateItemType, title, pageConfig, linkConfig, isFormChanged,
                  subHeader, validate, saveForm, validationErrors, children
              }: Readonly<FormProps>) {

    const history = useHistory();
    const dispatch = useAppDispatch();
    const activeNavItem = useAppSelector(navItemSelector);
    const saveHasBeenClicked = useAppSelector(saveSelector);
    const [titlePrefix, setTitlePrefix] = useState("");
    const [navConfig, setNavConfig] = useState<NavItem[]>([]);
    const [showBanner, setShowBanner] = useState<boolean>(!!validationErrors.length);
    const [errors, setErrors] = useState<ValidationError[]>(validationErrors);
    const releaseToggles = useAppSelector(selectReleaseToggles);

    useEffect(() => {
        setTitlePrefix(estateItem?.trustId === "" ? "Add " : "Edit ");
    }, [estateItem]);

    useEffect(() => {
        setErrors(validationErrors);
        setShowBanner(!!validationErrors.length && saveHasBeenClicked);
    }, [validationErrors, saveHasBeenClicked]);

    useEffect(() => {
        if (estateItem) {
            setNavConfig(createNavConfig(pageConfig, linkConfig));

            if (!activeNavItem) {
                const pageId = history.location.pathname.split('/').pop();
                const urlPage = pageConfig.find(page => page.id === pageId);
                const activePage = urlPage ?? pageConfig[0];
                dispatch(setActiveNavItem(activePage));
            }
        }
    }, [linkConfig, pageConfig, estateItem]);

    const hasRequiredFieldsError = () => {
        return errors.some(error => error.type === ValidationErrorType.REQUIRED_FIELDS);
    }

    const Container = releaseToggles?.enableEstateFlowchartMVPRelease
        ? ScrollableContainer
        : 'div';

    return (
        <div className="form-container">
            <FormHeader title={titlePrefix + title}
                        estateItem={estateItem}
                        estateItemType={estateItemType}
                        subheader={subHeader}
                        formHasBeenEdited={isFormChanged}/>
            <div className="form-main">
                <FormNav navConfig={navConfig}
                         estateItemId={estateItem?.trustId}
                         validateForm={validate}
                         saveForm={saveForm}/>
                <Container id="form-scrollable-Container" className="form-body">
                    <div className="alert-banner-container">
                        {
                            showBanner && hasRequiredFieldsError() &&
                            <AlertBanner message={Labels.REQUIRED_FIELDS_MESSAGE_TEXT}
                                         detail={Labels.REQUIRED_FIELDS_DETAIL_TEXT}/>
                        }
                    </div>
                    <div className="form-body-content">
                        {Children.map(children, child =>
                            child
                        )}
                    </div>
                </Container>
            </div>
            <FormFooter navConfig={navConfig} validateForm={validate} saveForm={saveForm} pageConfig={pageConfig}/>
        </div>
    );
}

export default Form;